// src/components/Admin/HelpSupport.js
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  ExpandMore,
  Description,
  VideoLibrary,
  Phone,
  Email,
  Send,
  Search,
  ChevronRight,
  ConfirmationNumber,
  Visibility,
  Close as CloseIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useThemeMode } from '../../contexts/ThemeModeContext';

export default function HelpSupport({ variant }) {
  const location = useLocation();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const isPacs = variant === 'pacs' || (variant !== 'ris' && (
    location?.pathname?.includes('radiologist') ||
    location?.pathname?.includes('reader') ||
    location?.pathname?.includes('pacs')
  ));

  const colors = isPacs ? {
    primary: '#3E6AE1',
    secondary: '#34C3FF',
    iconBg: 'linear-gradient(45deg, #3E6AE1, #34C3FF)',
    iconColor: '#FFFFFF',
    btnBg: 'linear-gradient(45deg, #3E6AE1, #34C3FF)',
    btnHoverBg: 'linear-gradient(45deg, #3258C2, #29A8DF)',
    btnRadius: '12px',
    hoverBorder: '#3E6AE1',
    linkColor: '#3E6AE1',
  } : {
    primary: '#00665C',
    secondary: '#0D9488',
    iconBg: isDark ? 'rgba(13, 148, 136, 0.2)' : 'rgba(13, 148, 136, 0.12)',
    iconColor: '#0D9488',
    btnBg: '#00665C',
    btnHoverBg: '#004D47',
    btnRadius: '8px',
    hoverBorder: '#0D9488',
    linkColor: '#0D9488',
  };

  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const cardBorder = `1.5px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0'}`;
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#475569';
  const borderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0';

  const [tabValue, setTabValue] = useState(0);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [ticketData, setTicketData] = useState({
    subject: '',
    description: '',
    category: 'general',
    priority: 'medium',
  });

  const faqs = [
    {
      question: 'How does the AI-assisted diagnosis work?',
      answer: 'DycoVue uses advanced machine learning algorithms trained on millions of medical images to provide preliminary diagnostic suggestions. The AI analyzes imaging patterns and highlights areas of interest, but all final diagnoses must be confirmed by qualified radiologists.',
    },
    {
      question: 'How secure is patient data in DycoVue?',
      answer: 'DycoVue employs industry-leading security measures including end-to-end encryption, HIPAA compliance, regular security audits, and secure cloud storage with AWS S3. All data transmission is encrypted using TLS 1.3, and access is controlled through role-based permissions.',
    },
    {
      question: 'Can DycoVue integrate with our existing systems?',
      answer: 'Yes, DycoVue is designed to integrate seamlessly with existing PACS systems, RIS, and EMR platforms. We support DICOM standards and provide API access for custom integrations. Contact our support team for specific integration requirements.',
    },
    {
      question: 'What training is required to use DycoVue effectively?',
      answer: 'DycoVue is designed with an intuitive interface that requires minimal training. We provide comprehensive onboarding materials, video tutorials, and live training sessions. Most users become proficient within 1-2 hours of initial training.',
    },
    {
      question: 'What imaging modalities does DycoVue support?',
      answer: 'DycoVue supports all major imaging modalities including CT, MRI, X-Ray, Ultrasound, PET, and more. The system automatically processes DICOM files and provides appropriate viewing and analysis tools for each modality.',
    },
  ];

  const documentationCategories = [
    { title: 'Getting Started', icon: '🚀' },
    { title: 'User Guides', icon: '📖' },
    { title: 'API Documentation', icon: '⚙️' },
    { title: 'Compliance & Legal', icon: '⚖️' },
  ];

  const videoCategories = [
    { title: 'DycoVue Basics', icon: '🎬' },
    { title: 'Advanced AI Features', icon: '🤖' },
    { title: 'Report Generation', icon: '📄' },
    { title: 'Administrator Training', icon: '👨‍💼' },
  ];

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/tickets');
      const data = response.data?.tickets || (Array.isArray(response.data) ? response.data : []);
      setTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tabValue === 4) {
      fetchTickets();
    }
  }, [tabValue, fetchTickets]);

  const handleTicketSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/tickets', ticketData);
      setSuccess('Ticket submitted successfully! We will respond within 24 hours.');
      setTicketData({ subject: '', description: '', category: 'general', priority: 'medium' });
      setTicketDialogOpen(false);
      fetchTickets();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit ticket');
    } finally {
      setLoading(false);
    }
  };

  const ResourceCard = ({ icon, title, description }) => (
    <Card
      sx={{
        background: cardBg,
        borderRadius: '12px',
        border: cardBorder,
        boxShadow: 'none',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        fontFamily: '"Satoshi", sans-serif',
        '&:hover': {
          borderColor: colors.hoverBorder,
          transform: isPacs ? 'translateY(-2px)' : 'none',
          boxShadow: isPacs ? `0 6px 18px rgba(62, 106, 225, 0.15)` : 'none',
        },
      }}
    >
      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
        <Box display="flex" alignItems="flex-start" gap={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              background: colors.iconBg,
              color: colors.iconColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box flex={1}>
            <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '0.92rem', color: textColorPrimary, lineHeight: 1.3, mb: 0.25 }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.78rem', color: textColorSecondary, lineHeight: 1.35 }}>
              {description}
            </Typography>
            <Box display="flex" alignItems="center" mt={1} color={colors.linkColor}>
              <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '0.78rem' }}>
                Learn More
              </Typography>
              <ChevronRight sx={{ fontSize: 16 }} />
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderDocumentationTab = () => (
    <Box>
      <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '1.05rem', color: textColorPrimary, mb: 0.25 }}>
        Product Documentation
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.75rem', color: textColorSecondary, mb: 1.5 }}>
        Comprehensive guides and references for all DycoVue features
      </Typography>

      <Grid container spacing={1.5}>
        {documentationCategories.map((category, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <ResourceCard
              icon={<Description sx={{ fontSize: 20 }} />}
              title={category.title}
              description="Step-by-step instructions and best practices"
            />
          </Grid>
        ))}
      </Grid>

      <Box mt={1.5}>
        <Button
          variant="contained"
          sx={{
            borderRadius: '6px',
            background: colors.btnBg,
            fontFamily: '"Satoshi", sans-serif',
            fontWeight: 700,
            fontSize: '0.8rem',
            height: 32,
            px: 2.5,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': { background: colors.btnHoverBg, boxShadow: 'none' },
          }}
        >
          Browse All Documentation
        </Button>
      </Box>
    </Box>
  );

  const renderVideoResourcesTab = () => (
    <Box>
      <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '1.05rem', color: textColorPrimary, mb: 0.25 }}>
        Video Resources
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.75rem', color: textColorSecondary, mb: 1.5 }}>
        Learn through step-by-step video demonstrations
      </Typography>

      <Grid container spacing={1.5}>
        {videoCategories.map((category, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <ResourceCard
              icon={<VideoLibrary sx={{ fontSize: 20 }} />}
              title={category.title}
              description="Watch comprehensive video tutorials"
            />
          </Grid>
        ))}
      </Grid>

      <Box mt={1.5}>
        <Button
          variant="contained"
          sx={{
            borderRadius: '6px',
            background: colors.btnBg,
            fontFamily: '"Satoshi", sans-serif',
            fontWeight: 700,
            fontSize: '0.8rem',
            height: 32,
            px: 2.5,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': { background: colors.btnHoverBg, boxShadow: 'none' },
          }}
        >
          Browse All Tutorials
        </Button>
      </Box>
    </Box>
  );

  const renderContactTab = () => (
    <Box>
      <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '1.05rem', color: textColorPrimary, mb: 0.25 }}>
        Contact Support
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.75rem', color: textColorSecondary, mb: 1.5 }}>
        Get in touch with our support team for personalized assistance
      </Typography>

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              background: cardBg,
              borderRadius: '12px',
              border: cardBorder,
              boxShadow: 'none',
              fontFamily: '"Satoshi", sans-serif',
            }}
          >
            <CardContent sx={{ p: 2, textAlign: 'center', '&:last-child': { pb: 2 } }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  background: colors.iconBg,
                  color: colors.iconColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 1,
                }}
              >
                <Phone sx={{ fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '0.95rem', color: textColorPrimary, mb: 0.25 }}>
                Phone
              </Typography>
              <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '1.1rem', color: colors.linkColor, mb: 0.5 }}>
                1-800-DYCOVUE
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.75rem', color: textColorSecondary }}>
                Mon–Fri, 8am – 8pm ET
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            sx={{
              background: cardBg,
              borderRadius: '12px',
              border: cardBorder,
              boxShadow: 'none',
              fontFamily: '"Satoshi", sans-serif',
            }}
          >
            <CardContent sx={{ p: 2, textAlign: 'center', '&:last-child': { pb: 2 } }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  background: colors.iconBg,
                  color: colors.iconColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 1,
                }}
              >
                <Email sx={{ fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '0.95rem', color: textColorPrimary, mb: 0.25 }}>
                Email
              </Typography>
              <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '1.1rem', color: colors.linkColor, mb: 0.5 }}>
                support@dycovue.com
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.75rem', color: textColorSecondary }}>
                24/7 response
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box mt={1.5}>
        <Button
          variant="contained"
          startIcon={<Send sx={{ fontSize: 16 }} />}
          onClick={() => setTicketDialogOpen(true)}
          sx={{
            borderRadius: '6px',
            background: colors.btnBg,
            fontFamily: '"Satoshi", sans-serif',
            fontWeight: 700,
            fontSize: '0.8rem',
            height: 32,
            px: 2.5,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': { background: colors.btnHoverBg, boxShadow: 'none' },
          }}
        >
          Submit Feedback / Support Ticket
        </Button>
      </Box>
    </Box>
  );

  const renderFAQTab = () => (
    <Box>
      <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '1.05rem', color: textColorPrimary, mb: 0.25 }}>
        Frequently Asked Questions
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.75rem', color: textColorSecondary, mb: 1.5 }}>
        Answers to common questions about DycoVue
      </Typography>

      <TextField
        fullWidth
        size="small"
        placeholder="Search questions..."
        InputProps={{
          startAdornment: <Search sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />,
          sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' }
        }}
        sx={{
          mb: 1.5,
          '& .MuiOutlinedInput-root': { borderRadius: '8px' },
        }}
      />

      {faqs.map((faq, index) => (
        <Accordion
          key={index}
          sx={{
            mb: 1,
            borderRadius: '8px !important',
            border: cardBorder,
            '&:before': { display: 'none' },
            bgcolor: cardBg,
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore sx={{ fontSize: 18 }} />} sx={{ minHeight: 38, py: 0.25, '& .MuiAccordionSummary-content': { my: 0.25 } }}>
            <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '0.85rem', color: textColorPrimary }}>
              {faq.question}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1.2, px: 2 }}>
            <Typography variant="body2" sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.78rem', color: textColorSecondary }}>
              {faq.answer}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );

  const renderTicketsTab = () => {
    const ticketList = Array.isArray(tickets) ? tickets : [];
    const filteredTickets = ticketList.filter(ticket => {
      const q = (searchTerm || '').toLowerCase();
      const name = ticket.name || `${ticket.submittedBy?.firstName || ''} ${ticket.submittedBy?.lastName || ''}`.trim() || '';
      const email = ticket.email || ticket.submittedBy?.email || '';
      const subject = ticket.subject || '';
      return name.toLowerCase().includes(q) || email.toLowerCase().includes(q) || subject.toLowerCase().includes(q);
    });

    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Box>
            <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '1.05rem', color: textColorPrimary, mb: 0.25 }}>
              Tickets List
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.75rem', color: textColorSecondary }}>
              View all tickets generated in Help & Support
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<ConfirmationNumber sx={{ fontSize: 16 }} />}
            onClick={() => setTicketDialogOpen(true)}
            sx={{
              borderRadius: '6px',
              background: colors.btnBg,
              fontFamily: '"Satoshi", sans-serif',
              fontWeight: 700,
              fontSize: '0.8rem',
              height: 32,
              px: 2,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { background: colors.btnHoverBg, boxShadow: 'none' },
            }}
          >
            New Ticket
          </Button>
        </Box>

        <Card
          sx={{
            background: cardBg,
            borderRadius: '12px',
            border: cardBorder,
            boxShadow: 'none',
          }}
        >
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search tickets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />,
                sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' }
              }}
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              }}
            />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', py: 0.75 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', py: 0.75 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', py: 0.75 }}>Subject</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', py: 0.75 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', py: 0.75 }}>Date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', py: 0.75 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTickets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary', fontSize: '0.8rem' }}>
                        {loading ? 'Loading tickets...' : 'No tickets found.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTickets.map((ticket) => {
                      const name = ticket.name || `${ticket.submittedBy?.firstName || ''} ${ticket.submittedBy?.lastName || ''}`.trim() || 'Anonymous';
                      const email = ticket.email || ticket.submittedBy?.email || '—';

                      return (
                        <TableRow key={ticket._id} hover>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 0.75 }}>{name}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', py: 0.75 }}>{email}</TableCell>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.8rem', py: 0.75 }}>{ticket.subject || '—'}</TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Chip
                              label={ticket.status || 'Open'}
                              color={ticket.status === 'resolved' ? 'success' : ticket.status === 'in_progress' ? 'info' : 'warning'}
                              size="small"
                              sx={{ fontWeight: 700, fontSize: '0.72rem', height: 20 }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', py: 0.75 }}>
                            {ticket.createdAt
                              ? new Date(ticket.createdAt).toLocaleDateString()
                              : 'N/A'}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.75 }}>
                            <IconButton size="small">
                              <Visibility sx={{ fontSize: 16 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={1.5}>
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', lineHeight: 1.2, fontFamily: '"Satoshi", sans-serif' }}>
          Help & Support
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.72rem', fontFamily: '"Satoshi", sans-serif' }}>
          Find answers, get assistance, and learn more about DycoVue
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.25, borderRadius: '6px', fontSize: '0.75rem' }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1.5, py: 0.25, borderRadius: '6px', fontSize: '0.75rem' }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Card
        sx={{
          background: cardBg,
          borderRadius: '16px',
          border: cardBorder,
          boxShadow: 'none',
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{
              '& .MuiTab-root': {
                fontFamily: '"Satoshi", sans-serif',
                fontWeight: 600,
                textTransform: 'none',
              },
              '& .Mui-selected': {
                color: `${colors.linkColor} !important`,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: colors.linkColor,
              },
            }}
          >
            <Tab label="Documentation" />
            <Tab label="Video Resources" />
            <Tab label="Contact Support" />
            <Tab label="FAQs" />
            <Tab label="Tickets" />
          </Tabs>
        </Box>

        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          {tabValue === 0 && renderDocumentationTab()}
          {tabValue === 1 && renderVideoResourcesTab()}
          {tabValue === 2 && renderContactTab()}
          {tabValue === 3 && renderFAQTab()}
          {tabValue === 4 && renderTicketsTab()}
        </CardContent>
      </Card>

      {/* Ticket Dialog */}
      <Dialog
        open={ticketDialogOpen}
        onClose={() => setTicketDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '90vw', md: '640px' },
            maxWidth: '660px !important',
            maxHeight: '88vh',
            borderRadius: '16px',
            bgcolor: cardBg,
            backgroundImage: 'none',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${borderColor}`, bgcolor: cardBg }}>
          <Typography variant="subtitle1" sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, color: textColorPrimary, fontSize: '1rem' }}>
            Create Support Ticket
          </Typography>
          <IconButton onClick={() => setTicketDialogOpen(false)} size="small" sx={{ color: textColorSecondary }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleTicketSubmit} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
          <DialogContent sx={{ p: 2.5, overflowY: 'auto' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Subject"
                  value={ticketData.subject}
                  onChange={(e) => setTicketData({ ...ticketData, subject: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={ticketData.category}
                    label="Category"
                    onChange={(e) => setTicketData({ ...ticketData, category: e.target.value })}
                    sx={{ borderRadius: '10px' }}
                  >
                    <MenuItem value="general">General Inquiry</MenuItem>
                    <MenuItem value="technical">Technical Issue</MenuItem>
                    <MenuItem value="bug_report">Bug Report</MenuItem>
                    <MenuItem value="feature_request">Feature Request</MenuItem>
                    <MenuItem value="billing">Billing</MenuItem>
                    <MenuItem value="account">Account</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={ticketData.priority}
                    label="Priority"
                    onChange={(e) => setTicketData({ ...ticketData, priority: e.target.value })}
                    sx={{ borderRadius: '10px' }}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={3.5}
                  label="Description"
                  inputProps={{ minLength: 10, maxLength: 5000 }}
                  value={ticketData.description}
                  onChange={(e) => setTicketData({ ...ticketData, description: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}`, bgcolor: cardBg }}>
            <Button
              onClick={() => setTicketDialogOpen(false)}
              sx={{ borderRadius: colors.btnRadius, textTransform: 'none', fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                borderRadius: colors.btnRadius,
                background: colors.btnBg,
                fontFamily: '"Satoshi", sans-serif',
                fontWeight: 700,
                px: 2.5,
                '&:hover': { background: colors.btnHoverBg },
              }}
            >
              {loading ? 'Submitting...' : 'Submit Ticket'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}